import { Component, DestroyRef, EventEmitter, inject, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { BaseService } from '../../../../../services/base.service';
import { BoardService } from '../../../../../services/board.service';
import { NotificationService } from '../../../../../services/notifications.service';
import { CreateCardReminderRequest } from '../../../../../interfaces/card';
import { CardReminderXData } from '../../../board-v3.types';
import { CardManagerOption } from '../card-general-panel.component';

export interface CardReminderPrefill {
  data: CardReminderXData;
  editing: boolean;
}

@Component({
  selector: 'app-board-v3-card-case-panel',
  templateUrl: './card-case-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BoardV3CardCasePanelComponent {
  @Input() cardId!: string;
  @Input() managers: CardManagerOption[] = [];
  @Output() reminderAdded = new EventEmitter<unknown>();

  private readonly boardService = inject(BoardService);
  private readonly notificationService = inject(NotificationService);
  private readonly baseService = inject(BaseService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly currentUserId: string | number | null =
    this.baseService.currentUserInfo?.id ?? this.baseService.user?.id ?? null;

  reminderTitleControl = new FormControl('');
  reminderControl = new FormControl('');
  reminderDateControl = new FormControl('');
  reminderTimeControl = new FormControl({ value: '', disabled: true });
  reminderAssignedControl = new FormControl<string | number | null>(this.currentUserId);
  submittingReminder = false;
  readonly reminderOffsetOptions: Array<{ value: number; labelKey: string }> = [
    { value: 0, labelKey: 'reminder_offset_at_time' },
    { value: 15, labelKey: 'reminder_offset_15m' },
    { value: 30, labelKey: 'reminder_offset_30m' },
    { value: 60, labelKey: 'reminder_offset_1h' },
    { value: 120, labelKey: 'reminder_offset_2h' },
    { value: 1440, labelKey: 'reminder_offset_1d' },
  ];
  selectedReminderOffsets = new Set<number>([0, 15]);
  editingReminderId: string | null = null;

  @Input()
  set prefill(value: CardReminderPrefill | null) {
    if (!value) return;

    this.editingReminderId = value.editing ? value.data.card_reminder_id : null;
    this.applyReminderPrefill(value.data);
  }

  private applyReminderPrefill(data: CardReminderXData): void {
    this.reminderTitleControl.setValue(data.title || '');
    this.reminderControl.setValue(data.text || '');
    this.reminderAssignedControl.setValue(data.assigned_id ?? this.currentUserId);

    const dueDate = new Date(data.due_at);
    if (Number.isNaN(dueDate.getTime())) return;

    const pad = (value: number): string => String(value).padStart(2, '0');
    this.reminderDateControl.setValue(
      `${dueDate.getFullYear()}-${pad(dueDate.getMonth() + 1)}-${pad(dueDate.getDate())}`,
    );
    this.reminderTimeControl.enable();
    this.reminderTimeControl.setValue(`${pad(dueDate.getHours())}:${pad(dueDate.getMinutes())}`);

    const offsets = (data.reminders || []).map((reminderAt) => {
      const diffMinutes = Math.round((dueDate.getTime() - new Date(reminderAt).getTime()) / 60000);

      return this.reminderOffsetOptions.reduce(
        (closest, option) =>
          Math.abs(option.value - diffMinutes) < Math.abs(closest - diffMinutes) ? option.value : closest,
        this.reminderOffsetOptions[0].value,
      );
    });

    this.selectedReminderOffsets = new Set<number>(offsets.length ? offsets : [0, 15]);
  }

  isReminderOffsetSelected(value: number): boolean {
    return this.selectedReminderOffsets.has(value);
  }

  toggleReminderOffset(value: number): void {
    if (this.selectedReminderOffsets.has(value)) {
      this.selectedReminderOffsets.delete(value);
    } else {
      this.selectedReminderOffsets.add(value);
    }
  }

  onReminderDateChange(): void {
    if (this.reminderDateControl.value) {
      this.reminderTimeControl.enable();
    } else {
      this.reminderTimeControl.reset('');
      this.reminderTimeControl.disable();
    }
  }

  submitReminder(): void {
    const title = this.reminderTitleControl.value?.trim();
    const dueAt = this.buildDueAtIso();
    if (!title || !dueAt || this.submittingReminder) return;

    const text = this.reminderControl.value?.trim();
    const assignedId = this.reminderAssignedControl.value;
    const dueDate = new Date(dueAt);
    const reminders = Array.from(this.selectedReminderOffsets).map((offsetMinutes) =>
      new Date(dueDate.getTime() - offsetMinutes * 60000).toISOString(),
    );

    const payload: CreateCardReminderRequest = {
      title,
      due_at: dueAt,
      ...(text ? { text } : {}),
      ...(assignedId ? { assigned_id: String(assignedId) } : {}),
      ...(reminders.length ? { reminders } : {}),
    };

    const request$ = this.editingReminderId
      ? this.boardService.updateCardReminder(this.cardId, this.editingReminderId, payload)
      : this.boardService.createCardReminder(this.cardId, payload);

    this.submittingReminder = true;
    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (event) => {
        this.reminderTitleControl.setValue('');
        this.reminderControl.setValue('');
        this.reminderDateControl.setValue('');
        this.reminderTimeControl.reset('');
        this.reminderTimeControl.disable();
        this.reminderAssignedControl.setValue(this.currentUserId);
        this.selectedReminderOffsets = new Set<number>([0, 15]);
        this.editingReminderId = null;
        this.submittingReminder = false;
        this.reminderAdded.emit(event);
      },
      error: () => {
        this.submittingReminder = false;
        this.notificationService.showError('', 'Failed to add case');
      },
    });
  }

  private buildDueAtIso(): string | null {
    if (!this.reminderDateControl.value || !this.reminderTimeControl.value) return null;

    const [year, month, day] = String(this.reminderDateControl.value).split('-').map(Number);
    const [hours, minutes] = String(this.reminderTimeControl.value).split(':').map(Number);
    const date = new Date(year, month - 1, day, hours, minutes);

    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }
}
